<template>
  <div
    class="p-3 border rounded items-center gap-3 flex justify-between"
    :class="backgroundColor"
  >
    <div class="flex w-full">
      <div class="flex min-w-40 shrink items-center justify-between">
        <icon-base
          v-if="showLoadingSpinner"
          :icon="IconLoadingCircle"
          class="mr-4 h-5 w-5 animate-spin rounded-full text-primary"
          view-box="0 0 20 20"
        />
        <div v-if="isPreviewSupported" data-cy="file-preview">
          <img
            v-if="file.imagePreviewUrl"
            class="max-h-6 max-w-6"
            :src="file.imagePreviewUrl"
            :alt="t('COMMON.PREVIEW')"
            loading="lazy"
          />
          <icon-base
            v-else
            class="shrink-0"
            :icon="fileType"
            height="16"
            width="16"
            viewBox="0 0 46 46"
          />
        </div>
        <div class="flex min-w-0 shrink items-start pl-4">
          <span
            :title="file.name"
            class="mr-2 min-w-0 shrink grow text-sm font-medium break-words text-gray-800"
          >
            {{ file.name }}
          </span>
          <span v-if="!file.failed">{{ formattedFileSize }}</span>
          <span v-else class="text-red-500">
            {{ $t("DEALS.DOCUMENTS.UPLOAD_FAILED") }}
          </span>
        </div>
      </div>
      <div class="flex shrink grow items-center justify-end">
        <icon-base
          v-if="!file.uploaded"
          class="ml-4 cursor-pointer"
          :icon="IconClear"
          height="14"
          width="14"
          data-cy="uploaded-document-remove"
          @click="cancel"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { formatFileSize, getDocumentFileType } from "@/helpers/formatting";

import IconClear from "@/components/icons/IconClear.vue";
import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";

import type { FileWBlob } from "@/models/common";

const emit = defineEmits<{
  "file:cancel": [id: string];
}>();

const { file } = defineProps<{
  file: FileWBlob;
  showLoadingSpinner?: boolean;
}>();

const { t } = useI18n();

const fileType = computed(() => getDocumentFileType(file.name));

const isPreviewSupported = computed(() => fileType.value !== "heic");

const formattedFileSize = computed(() => {
  const { unit, size } = formatFileSize(file.size);
  return t(`DEALS.DOCUMENTS.UPLOAD_FILE_SIZE_${unit}`, {
    size: size,
  });
});

const backgroundColor = computed(() => {
  if (file.failed) {
    return "bg-red-100";
  }
  if (file.uploaded) {
    return "bg-green-100";
  }
  return "bg-blue-100";
});

const cancel = () => {
  if (file.id) {
    emit("file:cancel", file.id);
  }
};
</script>
